import {
  Component,
  effect,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { KeyValuePipe, NgClass, NgForOf } from '@angular/common';
import { Chart } from 'chart.js';
import {
  LegendPositions,
  WidgetType,
} from '../../../services/grid-widget-config.service';
import { WidgetHelperService } from '../../../services/widget/widget-helper.service';
import { updateAlpha } from '../../../utility/color.utility';
import { BadgeModule } from 'primeng/badge';
import { WidgetStore } from '../../../stores/grid/widget.state';
import { getState } from '@ngrx/signals';
import { EntityId } from '@ngrx/signals/entities';

@Component({
  selector: 'app-chart-legend',
  standalone: true,
  imports: [KeyValuePipe, NgForOf, NgClass, BadgeModule],
  templateUrl: './chart-legend.component.html',
  styleUrl: './chart-legend.component.scss',
})
export class ChartLegendComponent implements OnInit, OnChanges {
  readonly widgetStore = inject(WidgetStore);
  @Input() datasets: any[] = [];
  @Input() type!: WidgetType;
  @Input() widgetId!: EntityId;
  @Input() legendGroups?: Record<string, string[]>;
  @Input() dataGroups?: any[];
  @Input() chartInstance!: Chart; // Pass the Chart.js instance from the parent
  @Input() legendPrefixValues?: string[];
  @Input() legendSuffixValues?: string[];
  @Input() legendPosition: LegendPositions = 'right';
  @Input() initialDisabledDatasets?: string[];
  @Input() initialDisabledDataPoints?: string[];
  @Output() datasetToggled = new EventEmitter<any>();
  @Output() dataPointToggled = new EventEmitter<string>();

  groupedLabels: Record<
    string,
    { label: string; color: string; index: number; key: string }[]
  > = {};
  isAxisLegend = false;
  axisLegendItems: { key: string; datasets: any[] }[] = [];

  constructor(private widgetHelper: WidgetHelperService) {
    effect(() => {
      const widgetState = getState(this.widgetStore);
      if (
        widgetState.rerenderId === this.widgetId ||
        widgetState.rerenderIds.includes(this.widgetId)
      ) {
        this.initDisabledStates();
      }
    });
  }

  /**
   * Lifecycle hook that is called after data-bound properties of a directive are initialized.
   * Performs the initialization of the chart legend, axis legend configuration,
   * and sets up disabled states for the widget.
   *
   * @return {void} Does not return a value.
   */
  ngOnInit(): void {
    this.isAxisLegend = this.widgetHelper.isSingleAxisChart(this.type);
    this.initLegend();
    this.initDisabledStates();
  }

  /**
   * Initializes the disabled states for datasets and data points
   * based on the provided initial disabled configurations.
   * It processes and applies the initial disabled datasets and data points
   * if they are defined and contain elements.
   *
   * @return {void} This method does not return a value.
   */
  initDisabledStates(): void {
    if (
      this.initialDisabledDatasets &&
      this.initialDisabledDatasets.length > 0
    ) {
      this.initDisabledDatasets(this.initialDisabledDatasets);
    }
    if (
      this.initialDisabledDataPoints &&
      this.initialDisabledDataPoints.length > 0
    ) {
      this.initDisabledDataPoints(this.initialDisabledDataPoints);
    }
  }

  /**
   * Groups chart labels into predefined legend groups, creating an organized structure.
   * Groups are matched based on existing legend groups and labels. Unmatched labels
   * are added to an "ungrouped" category. The grouped labels include corresponding
   * colors, indexes, and keys for each label.
   *
   * @return {void} No return value as the method modifies the `groupedLabels` property of the instance.
   */
  setGroupedLabels(): void {
    const labels: string[] =
      (this.chartInstance?.data?.labels as string[]) || [];
    const groups: any[] = (this.dataGroups as any[]) || [];
    const colors: string[] =
      (this.chartInstance?.data?.datasets[0]?.backgroundColor as string[]) ||
      [];

    // Map each group to its labels, colors, and indexes
    const grouped = this.legendGroups
      ? Object.entries(this.legendGroups).reduce(
          (acc, [group, groupLabels]) => {
            acc[group] = groupLabels
              .map(label => {
                const index = labels.indexOf(label);
                if (index !== -1) {
                  return {
                    label,
                    color: colors[index],
                    index,
                    key: groups[index]?.key,
                  };
                }
                return null; // Skip unmatched labels
              })
              .filter(item => item !== null) as {
              label: string;
              color: string;
              index: number;
              key: string;
            }[];
            return acc;
          },
          {} as Record<
            string,
            { label: string; color: string; index: number; key: string }[]
          >
        )
      : {};

    // Add unmatched labels to the "Other" group
    const matchedLabels = Object.values(grouped).flatMap(group =>
      group.map(item => item.label)
    );

    // add ungrouped labels
    const ungrouped = labels
      .map((label, index) => {
        if (!matchedLabels.includes(label)) {
          return {
            label,
            color: colors[index],
            index,
            key: groups[index]?.key,
          };
        }
        return null;
      })
      .filter(item => item !== null) as {
      label: string;
      color: string;
      index: number;
      key: string;
    }[];

    if (ungrouped.length > 0) {
      grouped['ungrouped'] = ungrouped;
    }

    this.groupedLabels = grouped;
  }

  /**
   * Responds to changes in the input properties bound to this component.
   * Automatically invoked by Angular when any data-bound input property changes.
   *
   * @param {SimpleChanges} changes - An object that represents the changes to the input properties.
   * Each property within the object corresponds to an input property of the component, and contains the current and previous values of the property.
   * @return {void} No return value as this method is used for handling changes.
   */
  ngOnChanges(changes: SimpleChanges): void {
    this.initLegend();
  }

  /**
   * Initializes the legend for the chart by determining whether
   * to set grouped labels or axis items based on the type of legend.
   *
   * @return {void} This method does not return any value.
   */
  initLegend(): void {
    if (!this.isAxisLegend) {
      this.setGroupedLabels();
    } else {
      this.setAxisItems();
    }
  }

  /**
   * Organizes datasets into grouped legend items based on their group property.
   * Datasets with the `hideInLegend` property set to true are excluded.
   * Updates the `axisLegendItems` property of the instance with the grouped legend items.
   *
   * @return {void} This method does not return any value.
   */
  setAxisItems(): void {
    this.axisLegendItems = this.datasets.reduce(
      (groups: { key: string; datasets: any[] }[], dataset: any) => {
        if (dataset.hideInLegend) return groups;
        const group = dataset.group || '';

        const lastGroup = groups[groups.length - 1];
        if (!lastGroup || lastGroup.key !== group) {
          groups.push({ key: group, datasets: [dataset] });
        } else {
          lastGroup.datasets.push(dataset);
        }
        return groups;
      },
      [] as { key: string; datasets: any[] }[]
    );
  }

  /**
   * Initializes the datasets marked as disabled by hiding them in the chart instance.
   *
   * @param {string[]} setKeys - An array of dataset keys that need to be marked as disabled (hidden).
   * @return {void} This method does not return any value.
   */
  initDisabledDatasets(setKeys: string[]): void {
    if (!this.chartInstance) return;

    this.chartInstance.data.datasets.forEach((dataset: any, index) => {
      const meta = this.chartInstance.getDatasetMeta(index);
      meta.hidden = setKeys.includes(dataset.labelKey);
    });

    this.chartInstance.update();
  }
  /**
   * Initializes the datasets marked as disabled by hiding them in the chart instance.
   *
   * @param {string[]} setKeys - An array of dataset keys that need to be marked as disabled (hidden).
   * @return {void} This method does not return any value.
   */
  initDisabledDataPoints(setKeys: string[]): void {
    if (!this.chartInstance) return;

    this.chartInstance.data.datasets.forEach((dataset: any, index) => {
      const meta = this.chartInstance.getDatasetMeta(index);
      dataset.dataKeys.forEach((key: string, pointIndex: number) => {
        const element = meta?.data[pointIndex] as any;
        if (element) {
          element.hidden = setKeys.includes(key);
        }
      });
    });

    this.chartInstance.update();
  }

  /**
   * toggle visibility-state using chart.js built-in functionality by emitter
   * @param dataset
   */
  toggleDataset(dataset: any) {
    this.datasetToggled.emit(dataset);
    if (!this.chartInstance) return;
    this.chartInstance.data.datasets.forEach((ds: any, index) => {
      if (ds.labelKey === dataset.labelKey) {
        const meta = this.chartInstance.getDatasetMeta(index);
        meta.hidden = !meta.hidden;
      }
    });
    this.chartInstance.update();
  }

  /**
   * Toggles the visibility of a data point in the chart for a specific dataset group.
   *
   * @param {number} index - The index of the data point to toggle.
   * @param {any} group - The group to which the data point belongs.
   * @return {void} Does not return a value.
   */
  toggleDataPoint(index: number, group: any): void {
    this.dataPointToggled.emit(group.key);
    const datasets = this.chartInstance?.data?.datasets || [];

    datasets.forEach((dataset: any, datasetIndex) => {
      const meta = this.chartInstance?.getDatasetMeta(datasetIndex);
      const element = meta?.data[dataset.dataKeys.indexOf(group.key)] as any;

      if (element) {
        element.hidden = !element.hidden; // Toggle hidden state
      }
    });

    this.chartInstance.update();
  }

  /**
   * Determines whether the specified dataset is hidden or visible in the chart instance.
   *
   * @param {any} dataset - The dataset to be checked for its*/
  isDatasetHidden(dataset: any): boolean {
    if (this.chartInstance) {
      const datasetIndex = this.datasets.indexOf(dataset);
      return this.chartInstance.getDatasetMeta(datasetIndex).hidden;
    }
    return false;
  }

  /**
   * Determines whether a chart element or dataset is hidden based on the provided parameters.
   *
   * @param {number} index - The index of the data element to check for visibility.
   *                         For pie/doughnut charts, this refers to the data point index.
   * @param {any} [dataset] - The dataset to check visibility for, applicable to bar/line charts.
   *                          This is optional and may not be required for pie/doughnut charts.
   */
  isHidden(index: number, dataset?: any, firstLevel = false): boolean {
    if (!this.isAxisLegend && !firstLevel) {
      // For pie/doughnut charts, check data point visibility
      const meta = this.chartInstance?.getDatasetMeta(0); // Single dataset
      const element = meta?.data[index] as any; // Type assertion to allow access to `hidden`
      return element?.hidden || false; // Check if the element is hidden
    } else {
      // For bar/line charts, check dataset visibility
      const datasetIndex = this.datasets.indexOf(dataset);
      return this.chartInstance?.getDatasetMeta(datasetIndex)?.hidden || false;
    }
  }

  /**
   * Retrieves the color for a dataset by updating its alpha value to full opacity.
   *
   * @param {string} color - The input color that needs its alpha value adjusted.
   * @return {string | string[]} The color with its alpha channel set to full opacity.
   */
  getDatasetColor(color: string): string | string[] {
    return updateAlpha(color, 1);
  }
}
